import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getProductId } from 'features/product-info';
import { getDeviceType } from 'common/selectors/device-type';
import { NotificationTemplates } from 'common/components/notification-template-factory/types';
import { showNotification, WishlistCheckboxButton } from '@navi-toolbox/ui-components';
import {
  ADD_TO_SHOPPING_LIST_REQUEST,
  getIsInWishList,
  REMOVE_FROM_SHOPPING_LIST_REQUEST,
} from 'features/shopping-list';
import { RootState } from 'typesafe-actions';

export type Props = {
  productDataIsLoading: boolean;
};

export const WishlistButton: React.FC<Props> = ({ productDataIsLoading }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const deviceType = useSelector(getDeviceType);
  const productId = useSelector(getProductId) || '';
  const isInWishList = useSelector((state: RootState) => getIsInWishList(state, productId));

  const addToShoppingList = (id: string) => dispatch(ADD_TO_SHOPPING_LIST_REQUEST(id));
  const removeFromShoppingList = (id: string) => dispatch(REMOVE_FROM_SHOPPING_LIST_REQUEST(id));

  const onShoppingListToggle = (nextInShoppingList: boolean) => {
    if (productId) {
      if (nextInShoppingList) {
        addToShoppingList(productId);
      } else {
        removeFromShoppingList(productId);
      }
    }
  };

  const text = isInWishList
    ? t('ui.buttons.shopping-list-checkbox-label-checked')
    : t('ui.buttons.shopping-list-checkbox-label');

  const tooltipText = isInWishList
    ? t('ui.buttons.shopping-list-tooltip-remove')
    : t('ui.buttons.shopping-list-tooltip-add');

  const showAddNotification = () => {
    showNotification({
      templateId: NotificationTemplates.WishList,
      data: {
        text: t('ui.buttons.shopping-list-snack-add'),
        buttonText: t('ui.buttons.shopping-list-button-go-to'),
        buttonHref: '/shopping-list/',
        inWishList: true,
      },
    });
  };

  const showRemoveNotification = () => {
    showNotification({
      templateId: NotificationTemplates.WishList,
      data: {
        text: t('ui.buttons.shopping-list-snack-remove'),
        buttonText: t('ui.buttons.shopping-list-button-revert'),
        inWishList: false,
        onClick: () => {
          showAddNotification();
          onShoppingListToggle(true);
        },
      },
    });
  };

  const handleAddClick = () => {
    showAddNotification();
    onShoppingListToggle(true);
  };

  const handleRemoveClick = () => {
    showRemoveNotification();
    onShoppingListToggle(false);
  };

  const handleClick = isInWishList ? handleRemoveClick : handleAddClick;

  return (
    <WishlistCheckboxButton
      isInteractive={!!productId && !productDataIsLoading}
      text={text}
      tooltipText={tooltipText}
      isDesktop={deviceType === 'desktop'}
      size="l"
      isChecked={isInWishList}
      onClick={handleClick}
    />
  );
};
